import React from 'react';
import PropTypes from 'prop-types';
import CartProp from '../propTypes/CartProp';
import PlanProp from '../propTypes/PlanProp';
import CategoryProp from '../propTypes/CategoryProp';
import { displayMoney } from '../../../services/utilityFunctions';
import {
  getPriceForUsers,
  calcMonthlyTotal,
  calcCurrentTotal,
  splitDiscounts,
  calcMonthlyPostTax,
  calcCurrentPostTax,
  calcTax,
  getPriceOfProduct,
} from '../../../services/cartCalculations';
import translate from '../../../commonComponents/translate';
import PriceLine from './PriceLine';

function Cart(props) {
  const {
    t,
    plansAreFree,
    plans,
    cart,
    categories,
    priceLabelClassName,
    priceValueClassName,
    totalContainerClassName,
    totalLineClassName,
    totalLabelClassName,
    totalValueClassName,
    onPackRemove,
    onProductRemove,
    onSubscriptionRemove,
    onDiscountRemove,
    interval,
    taxRatePercentage,
    isResellerInstance,
  } = props;

  const {
    numberUsers,
    numberAdmins,
    products: cartProds,
    packs: cartPacks,
    subscriptions: cartSubs,
    plan: cartPlan,
    discounts: cartDiscounts,
  } = cart;
  const renderPlanPrice = (plan, planType) => {
    const { _id, name, tiersUsers, tiersAdmins } = plan;
    const { price, isFlat } = getPriceForUsers(
      planType === 'admins' ? tiersAdmins : tiersUsers,
      planType === 'admins' ? numberAdmins : numberUsers,
    );

    let priceLabel = displayMoney(plansAreFree ? 0 : price);
    if (!plansAreFree && !isFlat) {
      priceLabel = `${
        planType === 'admins' ? numberAdmins : numberUsers
      } x ${priceLabel}`;
    }

    const planName = `${name}: ${
      planType === 'admins'
        ? t('subscriptionCreate_administrators')
        : t('subscriptionCreate_users')
    }`;

    return (
      <PriceLine
        key={_id + planType}
        labelClassName={priceLabelClassName}
        valueClassName={priceValueClassName}
        label={planName}
        value={priceLabel}
      />
    );
  };

  // eslint-disable-next-line react/prop-types
  const renderSubscriptionPrice = ({ product }) => {
    const { _id, name, isFreeTrial } = product;
    const { price, isFlat } = getPriceOfProduct(product, numberUsers);
    const isFree = plansAreFree && isFreeTrial;
    let priceLabel = displayMoney(isFree ? 0 : price);
    if (!isFree) {
      priceLabel = isFlat ? priceLabel : `${numberUsers} x ${priceLabel}`;
    }

    return (
      <PriceLine
        key={_id}
        labelClassName={priceLabelClassName}
        valueClassName={priceValueClassName}
        label={name}
        value={priceLabel}
        removeItem={() => onSubscriptionRemove(_id)}
      />
    );
  };

  // eslint-disable-next-line react/prop-types
  const renderPackPrice = ({ product, quantity }) => {
    const { _id, name, price } = product;

    let priceLabel = displayMoney(price);
    if (quantity > 1) {
      priceLabel = `${quantity} x ${priceLabel}`;
    }
    return (
      <PriceLine
        key={_id}
        labelClassName={priceLabelClassName}
        valueClassName={priceValueClassName}
        label={`${t('subscriptionCreate_nav_pack')}${name}`}
        value={priceLabel}
        removeItem={() => onPackRemove(_id)}
      />
    );
  };

  // eslint-disable-next-line react/prop-types
  const renderProductPrice = ({ product, quantity }) => {
    const { _id, name, mandatory } = product;
    const { price } = getPriceOfProduct(product, numberUsers);

    let priceLabel = displayMoney(price);
    if (quantity > 1) {
      priceLabel = `${quantity} x ${priceLabel}`;
    }
    return (
      <PriceLine
        key={_id}
        labelClassName={priceLabelClassName}
        valueClassName={priceValueClassName}
        label={name}
        value={priceLabel}
        removeItem={mandatory ? null : () => onProductRemove(_id)}
      />
    );
  };

  // eslint-disable-next-line react/prop-types
  const renderSubDiscount = discount => {
    const { _id, name, percentOff, amountOff } = discount;

    const priceLabel = amountOff ? displayMoney(amountOff) : `${percentOff} %`;
    return (
      <PriceLine
        key={_id}
        labelClassName={priceLabelClassName}
        valueClassName={priceValueClassName}
        label={name}
        value={`- ${priceLabel}`}
        removeItem={() => onDiscountRemove(_id)}
      />
    );
  };

  const monthlyPreTax = isResellerInstance
    ? 0
    : calcMonthlyTotal(cart, plans, plansAreFree);
  const monthlyTotal = isResellerInstance
    ? 0
    : calcMonthlyPostTax(cart, plans, plansAreFree, taxRatePercentage);
  const currentPreTax = isResellerInstance
    ? 0
    : calcCurrentTotal(cart, categories);
  const currentTotal = isResellerInstance
    ? 0
    : calcCurrentPostTax(cart, categories, taxRatePercentage);

  const { subs: subDiscs, prods: prodDiscs } = splitDiscounts(cartDiscounts);
  const hasPlans = !!cartPlan || cartSubs.length > 0 || subDiscs.length > 0;
  const hasProducts =
    cartPacks.length > 0 || cartProds.length > 0 || prodDiscs.length > 0;
  return (
    <>
      <div className="price-items-container">
        {hasPlans && (
          <h5>
            {interval === 'year'
              ? t('subscription_interval_year')
              : t('subscription_interval_month')}
          </h5>
        )}
        {!isResellerInstance && renderPlanPrice(cartPlan, 'users')}
        {!isResellerInstance && renderPlanPrice(cartPlan, 'admins')}
        {cartSubs.map(renderSubscriptionPrice)}
        {subDiscs.map(renderSubDiscount)}
        {!isResellerInstance && hasPlans && (
          <PriceLine
            labelClassName={priceLabelClassName}
            valueClassName={priceValueClassName}
            label={t('subscriptionCreate_nav_vat', `${taxRatePercentage}%`)}
            value={displayMoney(calcTax(monthlyPreTax, taxRatePercentage))}
          />
        )}
        {hasProducts && <h5>{t('subscriptionCreate_nav_now')}</h5>}
        {cartPacks.map(renderPackPrice)}
        {cartProds.map(renderProductPrice)}
        {prodDiscs.map(renderSubDiscount)}
        {!isResellerInstance && hasProducts && (
          <PriceLine
            labelClassName={priceLabelClassName}
            valueClassName={priceValueClassName}
            label={t('subscriptionCreate_nav_vat', `${taxRatePercentage}%`)}
            value={displayMoney(calcTax(currentPreTax, taxRatePercentage))}
          />
        )}
      </div>
      <div className={totalContainerClassName}>
        <h4 style={{ marginBottom: '0' }}>
          {t('subscriptionCreate_nav_totals')}
        </h4>
        <div className={totalLineClassName}>
          <div className={totalLabelClassName}>
            {interval === 'year'
              ? t('subscription_interval_year')
              : t('subscription_interval_month')}
          </div>
          <div className={totalValueClassName}>
            {displayMoney(monthlyTotal)}
          </div>
        </div>
        <div className={totalLineClassName}>
          <div className={totalLabelClassName}>
            {t('subscriptionCreate_nav_now')}
          </div>
          <div className={totalValueClassName}>
            {displayMoney(currentTotal)}
          </div>
        </div>
      </div>
    </>
  );
}

Cart.propTypes = {
  t: PropTypes.func.isRequired,
  plansAreFree: PropTypes.bool,
  cart: PropTypes.shape(CartProp.propType).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  plans: PropTypes.arrayOf(PropTypes.shape(PlanProp.propType)).isRequired,
  priceLabelClassName: PropTypes.string,
  priceValueClassName: PropTypes.string,
  totalContainerClassName: PropTypes.string,
  totalLineClassName: PropTypes.string,
  totalLabelClassName: PropTypes.string,
  totalValueClassName: PropTypes.string,
  onPackRemove: PropTypes.func.isRequired,
  onProductRemove: PropTypes.func.isRequired,
  onSubscriptionRemove: PropTypes.func.isRequired,
  onDiscountRemove: PropTypes.func.isRequired,
  interval: PropTypes.string.isRequired,
  taxRatePercentage: PropTypes.number.isRequired,
  isResellerInstance: PropTypes.bool.isRequired,
};

Cart.defaultProps = {
  plansAreFree: false,
  priceLabelClassName: 'prices-item-label',
  priceValueClassName: 'prices-item-value',
  totalContainerClassName: 'prices-total',
  totalLineClassName: 'prices-total-line',
  totalLabelClassName: 'prices-total-label',
  totalValueClassName: 'prices-total-value',
};

export default React.memo(translate(Cart));
